import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Check,
  Star,
  ArrowLeft,
  Sparkles,
  Bot,
  Users,
  Zap,
  Shield,
  BarChart3,
  Code2,
} from 'lucide-react';
import { Button } from '../components/Button';
import { useSubscription } from '../helpers/useSubscription';
import { useAuth } from '../helpers/useAuth';
import { postCheckout } from '../endpoints/subscription/checkout_POST.schema';
import Navbar from '../src/components/landing/Navbar';
import styles from './pricing.module.css';

import imgMultiAgent from '../src/assets/img-multi-agent.jpg';
import imgCollaboration from '../src/assets/img-collaboration.jpg';
import imgAutomation from '../src/assets/img-automation.jpg';
import imgSkills from '../src/assets/img-skills.jpg';
import imgVibeCoding from '../src/assets/img-vibe-coding.jpg';
import imgAnalytics from '../src/assets/img-analytics.jpg';

const plans = [
  {
    tier: 'free' as const,
    name: 'Free',
    monthly: 0,
    annual: 0,
    description: 'Get started with basic AI chat and single sessions',
    features: [
      'Basic AI assistance',
      'Single active sessions',
      'Community support',
      '100MB storage',
    ],
    cta: 'Current Plan',
    popular: false,
  },
  {
    tier: 'basic' as const,
    name: 'Basic',
    monthly: 30,
    annual: 25,
    description: 'Full AI chat and unlimited sessions for individuals',
    features: [
      'Full AI chat capabilities',
      'Unlimited sessions',
      'Priority support',
      '5GB storage',
      'Custom personas',
      'All templates',
    ],
    cta: 'Start 7-Day Trial',
    popular: false,
  },
  {
    tier: 'pro' as const,
    name: 'Pro',
    monthly: 45,
    annual: 35,
    description: 'Multi-agent workflows, TaskingBot, and collaboration',
    features: [
      'Everything in Basic',
      'Multi-agent orchestration',
      'Workflow automation',
      'TaskingBot assistant',
      'Session collaboration',
      '25GB storage',
      'Advanced analytics',
    ],
    cta: 'Start 7-Day Trial',
    popular: true,
  },
  {
    tier: 'enterprise' as const,
    name: 'Enterprise',
    monthly: 99,
    annual: 79,
    description: 'Full platform access with priority everything',
    features: [
      'Everything in Pro',
      'Priority support & SLA',
      'Unlimited storage',
      'Audit logs',
      'Custom AI training',
      'Dedicated account manager',
      'SSO integration',
    ],
    cta: 'Start 7-Day Trial',
    popular: false,
  },
];

const featureShowcase = [
  {
    icon: Users,
    title: 'Multi-Agent Swarm Orchestration',
    description:
      'Deploy parallel AI agents that plan, code, test, and ship simultaneously. Each agent specializes in a role — frontend, backend, DevOps, QA — working as a coordinated swarm team.',
    image: imgMultiAgent,
    accent: '#fb923c',
  },
  {
    icon: Code2,
    title: 'Swarm Vibe Coding Sessions',
    description:
      'Describe what you want in plain language. Multiple AI agents interpret your vision and build it in parallel — live preview updates in real-time as agents write code across files.',
    image: imgVibeCoding,
    accent: '#38bdf8',
  },
  {
    icon: Bot,
    title: 'TaskingBot Autonomous Agent',
    description:
      'Your always-on AI assistant with 10,000+ skills. TaskingBot runs scheduled tasks, monitors markets, posts to Discord/Slack, manages GitHub repos, and executes multi-step workflows autonomously.',
    image: imgSkills,
    accent: '#a78bfa',
  },
  {
    icon: Zap,
    title: 'Workflow Automation Engine',
    description:
      'Build complex automation pipelines with cron scheduling, webhook triggers, and multi-tool chains. Agents execute tasks on schedule — from daily reports to continuous deployment.',
    image: imgAutomation,
    accent: '#22c55e',
  },
  {
    icon: Shield,
    title: 'Session Collaboration & Governance',
    description:
      'Invite team members into live coding sessions with role-based access. Every AI action is logged for audit. Policy gates ensure code quality and security before any merge.',
    image: imgCollaboration,
    accent: '#f43f5e',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Running Board',
    description:
      'Real-time dashboard showing agent activity, task progress, and resource usage. Visual running board tracks every autonomous task from queue to completion with live status updates.',
    image: imgAnalytics,
    accent: '#eab308',
  },
];

export default function PricingPage() {
  const { data: subscription } = useSubscription();
  const { authState } = useAuth();
  const { hash } = useLocation();
  const isLoggedIn = authState.type === 'authenticated';
  const currentTier = subscription?.tier || 'free';
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');

  // Unlock body/root scroll for this standalone page (global.css locks both to overflow:hidden)
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Scroll to hash anchor on mount or hash change
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // Small delay to let DOM render
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hash]);

  const handleCheckout = async (tier: 'basic' | 'pro' | 'enterprise') => {
    try {
      const { url } = await postCheckout({ tier, billing });
      window.location.href = url;
    } catch (err: any) {
      alert(err.message || 'Failed to start checkout');
    }
  };

  return (
    <>
      <Navbar />
      <div className={styles.container}>
        <>
          <title>Pricing & Features | Tasking.tech</title>
          <meta
            name="description"
            content="Multi-agent AI swarm coding platform. Deploy parallel AI agents that plan, code, test, and ship simultaneously. Start with a 7-day free trial."
          />
        </>

        <Link to={isLoggedIn ? '/dashboard' : '/'} className={styles.backLink}>
          <ArrowLeft size={16} /> Back
        </Link>

        {/* ── Pricing Section ── */}
        <div id="pricing" className={styles.header}>
          <div className={styles.trialBadge}>
            <Sparkles size={14} /> 7-day free trial on all paid plans
          </div>
          <h1>
            Simple, transparent <span className={styles.gradientText}>pricing</span>
          </h1>
          <p>
            Start free and scale as you grow. Every paid plan includes a 7-day trial with full
            access.
          </p>

          <div style={{ marginTop: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', borderRadius: '9999px', border: '1px solid var(--border, #333)', padding: '4px' }}>
            <button
              onClick={() => setBilling('monthly')}
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                background: billing === 'monthly' ? 'var(--accent, #FF6B00)' : 'transparent',
                color: billing === 'monthly' ? '#fff' : 'var(--text-muted, #aaa)',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                background: billing === 'annual' ? 'var(--accent, #FF6B00)' : 'transparent',
                color: billing === 'annual' ? '#fff' : 'var(--text-muted, #aaa)',
              }}
            >
              Annual <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>(Save up to 22%)</span>
            </button>
          </div>
        </div>

        <div className={styles.grid}>
          {plans.map((plan) => {
            const isCurrent = currentTier === plan.tier;
            return (
              <div
                key={plan.tier}
                className={`${styles.card} ${plan.popular ? styles.popular : ''}`}
              >
                {plan.popular && !isCurrent && (
                  <div className={styles.popularBadge}>
                    <Star size={10} /> MOST POPULAR
                  </div>
                )}
                {isCurrent && isLoggedIn && <div className={styles.currentBadge}>CURRENT PLAN</div>}
                <h3 className={styles.tierName}>{plan.name}</h3>
                <div className={styles.price}>
                  <span className={styles.priceAmount}>${billing === 'annual' ? plan.annual : plan.monthly}</span>
                  {plan.monthly > 0 && <span className={styles.pricePeriod}>/month</span>}
                </div>
                {billing === 'annual' && plan.monthly > 0 && plan.annual < plan.monthly && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--accent, #FF6B00)', fontWeight: 500, marginTop: '2px' }}>
                    Save {Math.round(((plan.monthly - plan.annual) / plan.monthly) * 100)}% vs monthly
                  </p>
                )}
                <p className={styles.description}>{plan.description}</p>
                <ul className={styles.features}>
                  {plan.features.map((f) => (
                    <li key={f}>
                      <Check size={14} /> {f}
                    </li>
                  ))}
                </ul>
                {plan.tier === 'free' ? (
                  <Button
                    variant="outline"
                    className={styles.ctaButton}
                    disabled={isCurrent && isLoggedIn}
                    asChild={!isLoggedIn}
                  >
                    {isLoggedIn ? (
                      isCurrent ? (
                        'Current Plan'
                      ) : (
                        'Downgrade'
                      )
                    ) : (
                      <Link to="/login">Get Started Free</Link>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant={plan.popular ? 'default' : 'outline'}
                    className={styles.ctaButton}
                    disabled={isCurrent && isLoggedIn}
                    onClick={() => {
                      if (!isLoggedIn) {
                        window.location.href = '/login';
                        return;
                      }
                      if (!isCurrent) handleCheckout(plan.tier);
                    }}
                  >
                    {isCurrent && isLoggedIn ? 'Current Plan' : plan.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className={styles.trialNote}>
          All prices in USD. Cancel anytime during your trial — no charge.
        </p>

        <section id="repoforge" className={styles.addOnSection}>
          <div className={styles.addOnCard}>
            <div className={styles.addOnLabel}>Promotional Add-On</div>
            <h3>RepoForge Repository Access</h3>
            <p>Add-on feature for existing paid subscriptions (not a standalone plan).</p>
            <div className={styles.addOnPriceRow}>
              <span className={styles.addOnPrice}>$5.00</span>
              <span className={styles.addOnPeriod}>/month</span>
            </div>
          </div>
        </section>

        {/* ── Features Section ── */}
        <section id="features" className={styles.featuresSection}>
          <div className={styles.featuresHeader}>
            <h2>
              The multi-agent AI platform{' '}
              <span className={styles.gradientText}>built for teams</span>
            </h2>
            <p>
              Tasking.tech deploys swarms of specialized AI agents that work in parallel — planning,
              coding, testing, and shipping while you steer. Every action is auditable, every agent
              is governed by policy.
            </p>
          </div>

          <div className={styles.featuresGrid}>
            {featureShowcase.map((feature, i) => {
              const Icon = feature.icon;
              const isReversed = i % 2 === 1;
              return (
                <div
                  key={feature.title}
                  className={`${styles.featureRow} ${isReversed ? styles.featureRowReversed : ''}`}
                >
                  <div className={styles.featureContent}>
                    <div
                      className={styles.featureIcon}
                      style={{ '--feature-accent': feature.accent } as React.CSSProperties}
                    >
                      <Icon size={22} />
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <Link to={isLoggedIn ? '/dashboard' : '/login'} className={styles.featureCta}>
                      {isLoggedIn ? 'Open Dashboard' : 'Get Started'}{' '}
                      <span aria-hidden="true">&rarr;</span>
                    </Link>
                  </div>
                  <div className={styles.featureImage}>
                    <img src={feature.image} alt={feature.title} loading="lazy" />
                    <div
                      className={styles.featureImageGlow}
                      style={{
                        background: `radial-gradient(ellipse at center, ${feature.accent}18 0%, transparent 70%)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className={styles.bottomCta}>
          <h2>Ready to ship faster with AI swarm agents?</h2>
          <p>Start your 7-day free trial today. No credit card required.</p>
          <div className={styles.bottomCtaButtons}>
            <Button
              variant="default"
              onClick={() => {
                window.location.href = isLoggedIn ? '/dashboard' : '/login';
              }}
              className={styles.ctaButtonLarge}
            >
              {isLoggedIn ? 'Go to Dashboard' : 'Start Free Trial'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={styles.ctaButtonLarge}
            >
              Explore Features
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
